import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FormBaseColumn } from '@/components/Form/Form.styles'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Heading4 } from '@/components/ui/typography'
import { prefixFilterValues } from '@/routes/Records/IB/Rebates/helpers/prefixFilterValues'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import { FieldSettings, Filter, FilterChangeEvent } from '@progress/kendo-react-data-tools'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { Tooltip } from 'antd'
import { NameDto } from 'api/Common/Types'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { IntroducingBrokerDetailsDto } from 'api/Trading-Accounts/Types/IntroducingBrokerDetailsDto'
import {
  IBIndividualRebate,
  IBIndividualRebateData,
  IntroducingBrokerRebate,
} from 'api/Trading-Accounts/Types/IntroducingBrokerRebates'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { FilterCell } from 'components/Filter/FilterCell'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { errorToast, ToastContext } from 'components/Toast/Toast'
import { useSingleGridState } from 'features/DataGrid/hooks/useGridState'
import { Search } from 'lucide-react'
import { pageableSettings } from 'store/grid/grid.slice'
import { ResponseError, useApiClient } from 'utils/api/ApiClient'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery } from 'utils/QueryHelpers'

import { AddRebateModal } from './modals/AddRebateModal'
import { AddRebateToGroupModal } from './modals/AddRebateToGroupModal'
import { EditRebateModal } from './modals/EditRebateModal'

interface Props {
  data: IntroducingBrokerDetailsDto
  refreshGroups?: () => void
  isEdit: boolean
  groups: NameDto<string>[]
  taFilter?: CompositeFilterDescriptor
  onTaFilterChange?: (event: FilterChangeEvent) => void
  taFilterFields?: FieldSettings[]
}

interface EditModalState {
  visible: boolean
  data: IBIndividualRebateData | null
  tradingAccount: NameDto<string> | null
}

export const IBRebatesTASettings = forwardRef((props: Props, ref) => {
  const { data, isEdit, groups, refreshGroups, taFilter, onTaFilterChange, taFilterFields } = props
  const { gridState, gridActions } = useSingleGridState()
  type ModalOption =
    | 'add-rebate'
    | 'add-ta-to-group'
    | 'remove-rebate-from-group'
    | 'delete-rebate'
    | ''
  const setToast = useContext(ToastContext)
  const [visibleModal, setVisibleModal] = useState<ModalOption>()
  const [deleteModal, setDeleteModal] = useState<IntroducingBrokerRebate>()
  const [editModal, setEditModal] = useState<EditModalState>({
    visible: false,
    tradingAccount: null,
    data: null,
  })
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []
  const [dataSelected, setDataSelected] = useState<IntroducingBrokerRebate[]>([])

  const taFilterRef = useRef<CompositeFilterDescriptor | undefined>(undefined)
  useEffect(() => {
    taFilterRef.current = props.taFilter
  }, [props.taFilter])

  const { callback: rebatesCallback, forceRefresh } = useCallbackWithForceRefresh(() => {
    const currentFilter = taFilterRef.current
    const prefixedFilter = currentFilter ? prefixFilterValues(currentFilter, data.id) : null
    const searchQuery = prefixedFilter ? (buildQuery(prefixedFilter) ?? null) : null

    return partnershipsApiClient.getIntroducingBrokerRebates(data.id, {
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      search: searchQuery,
    })
  }, [data.id, gridState])

  useImperativeHandle(ref, () => ({
    refreshRebates() {
      forceRefresh()
    },
    resetSelection() {
      setDataSelected([])
    },
  }))

  const { data: rebates, isLoading } = useFetchOne(rebatesCallback)

  const handleFilterFetch = () => {
    forceRefresh()
  }

  const handleGridPageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip,
      recordsPerPage: take,
    })

    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()
    gridActions.setGridFilters({ take, skip, pageIndex, pageSize })
  }

  const createRebate = async (rebateData: IBIndividualRebate) => {
    try {
      setVisibleModal(undefined)
      await partnershipsApiClient.createIBIndividaulRebate(data.id, rebateData)
    } catch (e) {
      if (e instanceof ResponseError) {
        const errorData = e.response.response?.data as { code: string }
        if (errorData?.code === 'trading_account_has_another_introducing_broker_rebate') {
          setToast(
            errorToast({ title: 'Error', subtitle: 'Please, detach TA from the Group first' })
          )
        }
      }
    } finally {
      gridActions.setGridFilters({ ...gridState })
    }
  }

  const addTAToGroup = async (tradingAccountIds: string[], groupId: string) => {
    try {
      setVisibleModal(undefined)
      await partnershipsApiClient.addIBTAToGroup(data.id, tradingAccountIds, groupId)
    } catch (e) {
      if (e instanceof ResponseError) {
        const errorData = e.response.response?.data as { code: string }
        if (errorData?.code === 'trading_account_has_another_introducing_broker_rebate') {
          setToast(
            errorToast({
              title: 'Error',
              subtitle: 'Please, remove individual rebate settings before adding it to the group',
            })
          )
        }
      }
    } finally {
      refreshGroups?.()
      gridActions.setGridFilters({ ...gridState })
    }
  }

  const handleSelectionChange = (data: IntroducingBrokerRebate, checked?: boolean) => {
    if (checked) {
      setDataSelected([...dataSelected, data])
    } else {
      setDataSelected(
        dataSelected.filter((item) => item.tradingAccount.id !== data.tradingAccount.id)
      )
    }
  }

  const handleRemove = async () => {
    if (!dataSelected.length || !dataSelected[0].rebateGroup?.id) return
    setVisibleModal('')
    const groupId = dataSelected[0].rebateGroup?.id
    await partnershipsApiClient.removeIBTAsFromGroup(
      data.id,
      dataSelected.map(({ tradingAccount }) => tradingAccount.id),
      groupId
    )
    refreshGroups?.()
    gridActions.setGridFilters({ ...gridState })
  }

  const removeMessage = useMemo(() => {
    const isRemovable = new Set(dataSelected.map((obj) => obj.rebateGroup?.id)).size === 1

    if (!dataSelected.length) return ''
    if (!isRemovable) {
      return 'Selected TAs must belong to the same group'
    }
    if (!dataSelected[0].rebateGroup) {
      return 'Selected TAs have no group assigned'
    }
    return ''
  }, [dataSelected])

  const handleEdit = (item: IntroducingBrokerRebate) => {
    setEditModal({
      visible: true,
      data: {
        rebateApprovalTypeId: item.rebateApprovalType?.id,
        rebatePeriodId: item.rebatePeriod?.id,
        rebateAmountInPercentage: item.rebateAmountInPercentage,
        isDefault: item.isDefault,
      },
      tradingAccount: item.tradingAccount,
    })
  }

  const handleEditRebate = async (rebateData: IBIndividualRebateData, tradingAccountId: string) => {
    setVisibleModal('')
    await partnershipsApiClient.updateIBIndividualRebate(data.id, tradingAccountId, rebateData)
    gridActions.setGridFilters({ ...gridState })
    setEditModal({
      visible: false,
      tradingAccount: null,
      data: null,
    })
  }

  const handleRemoveIndividualRebate = async (item?: IntroducingBrokerRebate) => {
    if (!item) return
    try {
      await partnershipsApiClient.deleteIBIndividualRebate(data.id, item.tradingAccount.id)
    } catch (e) {
      console.error(e)
    } finally {
      refreshGroups?.()
      gridActions.setGridFilters({ ...gridState })
      setDeleteModal(undefined)
    }
  }

  return (
    <FormBaseColumn>
      {taFilter && onTaFilterChange && taFilterFields && (
        <div style={{ marginBottom: 16 }}>
          <Heading4 style={{ marginBottom: 8 }}>TA Settings Filters</Heading4>
          <Filter
            style={{ zIndex: 0 }}
            value={taFilter}
            onChange={onTaFilterChange}
            fields={taFilterFields}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <Button type='button' onClick={handleFilterFetch} data-test='ta-filter-search-button'>
              <Search />
              Filter TA
            </Button>
          </div>
        </div>
      )}
      {visibleModal === 'add-rebate' && (
        <AddRebateModal
          isOpen
          onClose={() => setVisibleModal(undefined)}
          introducingBrokerId={data.id}
          onSubmit={createRebate}
        />
      )}

      <Dialog
        open={editModal.visible}
        onOpenChange={(open) =>
          !open &&
          setEditModal({
            visible: false,
            tradingAccount: null,
            data: null,
          })
        }
      >
        <EditRebateModal
          rebate={editModal.data}
          tradingAccount={editModal.tradingAccount}
          onSubmit={handleEditRebate}
        />
      </Dialog>
      {visibleModal === 'add-ta-to-group' && (
        <AddRebateToGroupModal
          isOpen
          onClose={() => setVisibleModal(undefined)}
          groups={groups}
          tradingAccounts={dataSelected?.map(({ tradingAccount }) => tradingAccount)}
          introducingBrokerId={data.id}
          onSubmit={addTAToGroup}
        />
      )}
      <ConfirmationModal
        contentText={`Are you sure you want to remove the TA(s) (${dataSelected?.map(({ tradingAccount }) => tradingAccount.name).join(', ')}) from the corresponding group?`}
        yesButtonText='Yes, delete'
        onYes={handleRemove}
        onCancel={() => setVisibleModal('')}
        visible={visibleModal === 'remove-rebate-from-group'}
      />
      <ConfirmationModal
        contentText={`Are you sure you want to delete the rebate for the account ${deleteModal?.tradingAccount?.name}?`}
        yesButtonText='Yes'
        cancelButtonText='No'
        onYes={() => handleRemoveIndividualRebate(deleteModal)}
        onCancel={() => setDeleteModal(undefined)}
        visible={!!deleteModal}
      />

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <Heading4>TA Settings</Heading4>
        <div className='flex flex-wrap gap-2'>
          <Button
            type='button'
            onClick={() => setVisibleModal('add-rebate')}
            disabled={
              !hasPermission(permissions, PermissionsEnum.CanEditIntroducingBrokerRebate) || !isEdit
            }
            data-test='add-rebate-button'
          >
            Add Rebate
          </Button>
          <Button
            type='button'
            onClick={() => setVisibleModal('add-ta-to-group')}
            disabled={
              !hasPermission(permissions, PermissionsEnum.CanEditIntroducingBrokerRebate) || !isEdit
            }
            data-test='add-rebate-button'
          >
            Add to group
          </Button>
          <Tooltip title={removeMessage}>
            <Button
              type='button'
              onClick={() => setVisibleModal('remove-rebate-from-group')}
              disabled={
                !hasPermission(permissions, PermissionsEnum.CanEditIntroducingBrokerRebate) ||
                !isEdit ||
                !dataSelected.length ||
                !!removeMessage
              }
              data-test='add-rebate-button'
            >
              Remove from group
            </Button>
          </Tooltip>
        </div>
      </div>
      <Loading isLoading={isLoading}>
        <Grid
          {...gridState}
          data={rebates?.items}
          pageable={pageableSettings}
          onPageChange={handleGridPageChange}
          sort={gridState.sort}
          selectedField='selected'
          total={rebates?.totalResults}
          take={gridState.take}
          skip={gridState.skip}
        >
          <Column
            field='selected'
            width='45px'
            cell={({ dataItem }) => (
              <td>
                <input
                  disabled={!isEdit}
                  type='checkbox'
                  checked={
                    isEdit &&
                    dataSelected?.some(
                      ({ tradingAccount }) => tradingAccount.id === dataItem.tradingAccount.id
                    )
                  }
                  onChange={(event) => handleSelectionChange(dataItem, event.target.checked)}
                />
              </td>
            )}
            headerCell={() => <th></th>}
          />
          <Column
            className='table-columns__content'
            field='tradingAccount.name'
            title='Trading Account'
          />
          <Column
            className='table-columns__content'
            field='rebateApprovalType.name'
            title='Rebate Type'
          />
          <Column
            className='table-columns__content'
            field='rebatePeriod.name'
            title='Rebate Period'
          />
          <Column
            className='table-columns__content'
            field='rebateAmountInPercentage'
            title='Rebate Amount, %'
          />
          <Column
            className='table-columns__content'
            field='rebateGroup.name'
            title='Group'
            cell={(props: GridCellProps) => <td>{props.dataItem.rebateGroup?.name || '-'}</td>}
          />
          {hasPermission(permissions, PermissionsEnum.CanEditIntroducingBrokerRebate) && (
            <Column
              width={50}
              title=''
              filterCell={FilterCell}
              cell={(props: GridCellProps) => (
                <td>
                  <MoreButton
                    disabled={!isEdit}
                    items={[
                      {
                        name: 'Edit',
                        'data-test': 'edit',
                        disabled:
                          !!props.dataItem?.rebateGroup?.id ||
                          !props.dataItem?.rebateApprovalType?.id,
                        onClick: () => handleEdit(props.dataItem),
                      },
                      {
                        name: 'Remove',
                        'data-test': 'remove',
                        disabled:
                          !!props.dataItem?.rebateGroup?.id ||
                          !props.dataItem?.rebateApprovalType?.id,
                        onClick: () => setDeleteModal(props.dataItem),
                      },
                    ]}
                    data-test='more-button'
                  />
                </td>
              )}
            />
          )}
        </Grid>
      </Loading>
    </FormBaseColumn>
  )
})
